import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, useAuth, anonymize } from "../App";
import { AlertTriangle, ChevronLeft, ChevronRight, Gavel, Loader2, Printer } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { BenchMonthGrid, BenchEntriesList } from "../components/BenchCalendarParts";
import { ReassignDialog } from "../components/ReassignDialog";
import { printBenchCalendar } from "../components/benchCalendarPrint";
import { RoleScopeBanner } from "../components/RoleScopeBanner";
import { docketKey } from "../components/DocketButton";

const MONTHS = ["Ιανουάριος", "Φεβρουάριος", "Μάρτιος", "Απρίλιος", "Μάιος", "Ιούνιος", "Ιούλιος", "Αύγουστος", "Σεπτέμβριος", "Οκτώβριος", "Νοέμβριος", "Δεκέμβριος"];
const pad = (n) => String(n).padStart(2, "0");

export default function BenchCalendar() {
  const { user } = useAuth();
  const privacy = user?.privacy_mode || false;
  const p = (t) => privacy ? anonymize(t) : t;
  const isJudge = user?.role === "judge";
  const isAdmin = user?.role === "admin";
  const userCourtTypes = (user?.court_types && user.court_types.length) ? user.court_types : ["Μονομελές", "Πολυμελές"];
  const courtTypeOptions = isAdmin ? ["Μονομελές", "Πολυμελές"] : userCourtTypes;
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [judge, setJudge] = useState("");
  const [branch, setBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [courtType, setCourtType] = useState(() => courtTypeOptions.length === 1 ? courtTypeOptions[0] : "");
  const [includeUnassigned, setIncludeUnassigned] = useState(true);
  const [data, setData] = useState({ judges: [], entries: [], total_entries: 0, total_cases: 0, unassigned_count: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [judgesByCt, setJudgesByCt] = useState({ "Μονομελές": [], "Πολυμελές": [] });
  const [docketMap, setDocketMap] = useState({});
  const onDocketFetched = (d) => setDocketMap(m => ({ ...m, [docketKey(d.pinakio_name, d.hearing_date)]: { id: d.id, line_count: d.line_count, matched_count: (d.matched_case_ids || []).length, fetched_at: d.fetched_at } }));

  const dateFrom = `${cursor.y}-${pad(cursor.m + 1)}-01`;
  const dateTo = `${cursor.y}-${pad(cursor.m + 1)}-${pad(new Date(cursor.y, cursor.m + 1, 0).getDate())}`;

  useEffect(() => {
    axios.get(`${API}/bench-calendar/branches`).then(r => setBranches(r.data?.branches || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (isJudge) return;
    ["Μονομελές", "Πολυμελές"].forEach(ct => {
      axios.get(`${API}/pretrial-judges-list?court_type=${encodeURIComponent(ct)}`)
        .then(r => setJudgesByCt(prev => ({ ...prev, [ct]: r.data?.judges || [] })))
        .catch(() => {});
    });
  }, [isJudge, reloadKey]);

  useEffect(() => { setSelectedDate(null); }, [dateFrom, judge, courtType, branch, includeUnassigned]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
        if (judge) params.append("judge", judge);
        if (courtType) params.append("court_type", courtType);
        if (branch) params.append("branch", branch);
        if (!includeUnassigned) params.append("include_unassigned", "false");
        const r = await axios.get(`${API}/bench-calendar?${params.toString()}`, { timeout: 120000 });
        if (!cancelled) setData(r.data);
        axios.get(`${API}/solon/dockets/lookup`, { params: { date_from: dateFrom, date_to: dateTo } })
          .then(res => { if (cancelled) return; const m = {}; (res.data.items || []).forEach(it => { m[docketKey(it.pinakio_name, it.hearing_date)] = it; }); setDocketMap(m); })
          .catch(() => {});
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Σφάλμα φόρτωσης ημερολογίου έδρας");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, judge, courtType, branch, includeUnassigned, reloadKey]);

  const reload = () => setReloadKey(k => k + 1);

  const shownEntries = useMemo(
    () => selectedDate ? data.entries.filter(e => e.hearing_date === selectedDate) : data.entries,
    [data.entries, selectedDate]
  );
  const shiftMonth = (delta) => setCursor(({ y, m }) => { const d = new Date(y, m + delta, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const filterLabel = [branch || "Όλα τα καταστήματα", courtType || "Μονομελές & Πολυμελές", isJudge ? (data.locked_judge || "") : (judge || "Όλοι οι δικαστές έδρας")].filter(Boolean).join(" · ");
  const handlePrint = () => {
    const ok = printBenchCalendar({ year: cursor.y, month: cursor.m, entries: shownEntries, filters: filterLabel, privacyFn: p });
    if (!ok) toast.error("Ο browser μπλόκαρε το παράθυρο εκτύπωσης — επιτρέψτε τα pop-ups για αυτή τη σελίδα");
  };

  return (
    <div className="space-y-4" data-testid="bench-calendar-page">
      <RoleScopeBanner />
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Gavel className="w-5 h-5 text-amber-700" />Ημερολόγιο Έδρας</CardTitle>
          <CardDescription>
            {isJudge ? "Οι δικάσιμοι στις οποίες κάθεστε ως Δικαστής Έδρας." : "Δικάσιμοι ανά Δικαστή Έδρας — ανά πινάκιο και ημερομηνία. Με κόκκινο περίγραμμα οι δικάσιμοι χωρίς ορισμένο δικαστή έδρας. Κλικ σε ημέρα → ορισμός Δικαστή Έδρας (Μονομελές) ή Ανάθεση σε Σύνθεση & Δικαστή Έδρας (Πολυμελές) και ΑΝΑΧΡΕΩΣΗ απευθείας από τη λίστα."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => shiftMonth(-1)} data-testid="bench-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
              <span className="font-semibold text-sm min-w-[170px] text-center" data-testid="bench-month-label">{MONTHS[cursor.m]} {cursor.y}</span>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => shiftMonth(1)} data-testid="bench-next-month"><ChevronRight className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { const d = new Date(); setCursor({ y: d.getFullYear(), m: d.getMonth() }); }} data-testid="bench-today-btn">Σήμερα</Button>
            </div>
            {!isJudge && (
              <Select value={judge || "__all__"} onValueChange={(v) => setJudge(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-8 w-64 text-xs" data-testid="bench-judge-filter"><SelectValue placeholder="Όλοι οι δικαστές έδρας" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="__all__">Όλοι οι δικαστές έδρας</SelectItem>
                  {data.judges.map(j => <SelectItem key={j} value={j}>{j}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={branch || "__all__"} onValueChange={(v) => setBranch(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-8 w-56 text-xs" data-testid="bench-branch-filter"><SelectValue placeholder="Όλα τα καταστήματα" /></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="__all__">Όλα τα καταστήματα</SelectItem>
                {branches.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={courtType || "__all__"} onValueChange={(v) => setCourtType(v === "__all__" ? "" : v)} disabled={courtTypeOptions.length === 1}>
              <SelectTrigger className="h-8 w-44 text-xs" data-testid="bench-court-type-filter"><SelectValue placeholder="Μονομελές & Πολυμελές" /></SelectTrigger>
              <SelectContent>
                {courtTypeOptions.length > 1 && <SelectItem value="__all__">Μονομελές & Πολυμελές</SelectItem>}
                {courtTypeOptions.map(ct => <SelectItem key={ct} value={ct}>{ct}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="ml-auto flex items-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : (
                <>
                  <Badge variant="outline" className="border-amber-300 text-amber-800" data-testid="bench-total-entries">{data.total_entries} δικάσιμοι/πινάκια</Badge>
                  <Badge variant="outline" className="border-slate-300 text-slate-700" data-testid="bench-total-cases">{data.total_cases} υποθ.</Badge>
                  {!isJudge && includeUnassigned && (
                    <Badge className={`border-0 text-white ${data.unassigned_count ? "bg-red-600" : "bg-emerald-600"}`} data-testid="bench-unassigned-count">
                      {data.unassigned_count ? <AlertTriangle className="w-3 h-3 mr-1" /> : null}{data.unassigned_count} χωρίς δικαστή
                    </Badge>
                  )}
                </>
              )}
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={handlePrint} data-testid="bench-print-btn"><Printer className="w-3.5 h-3.5" />Εκτύπωση</Button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-2">
            <p className="text-xs text-slate-500" data-testid="bench-filter-label">Φίλτρο: <b>{filterLabel}</b></p>
            {!isJudge && (
              <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
                <Checkbox checked={includeUnassigned} onCheckedChange={(v) => setIncludeUnassigned(!!v)} data-testid="bench-include-unassigned" />
                Εμφάνιση δικασίμων <span className="text-red-700 font-semibold">χωρίς δικαστή έδρας</span>
              </label>
            )}
            <span className="text-[11px] text-slate-500 flex items-center gap-3 ml-auto">
              <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm border border-amber-400 bg-amber-50" />με δικαστή έδρας</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm border-2 border-red-600 bg-red-50" />χωρίς δικαστή</span>
            </span>
          </div>
        </CardContent>
      </Card>

      <BenchMonthGrid year={cursor.y} month={cursor.m} entries={data.entries} showJudge={!judge && !isJudge}
                      selectedDate={selectedDate} onSelectDate={setSelectedDate} />

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">
          {selectedDate ? `Δικάσιμοι ${selectedDate.split("-").reverse().join("/")}` : `Όλες οι δικάσιμοι έδρας — ${MONTHS[cursor.m]} ${cursor.y}`}
        </h3>
        {selectedDate && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSelectedDate(null)} data-testid="bench-clear-day">Όλος ο μήνας</Button>}
      </div>
      <BenchEntriesList entries={shownEntries} privacyFn={p} canAssign={!isJudge} judgesByCt={judgesByCt} onReload={reload} onReassign={isJudge ? undefined : setReassignTarget}
                        docketMap={docketMap} onDocketFetched={onDocketFetched} />
      <ReassignDialog target={reassignTarget} onClose={() => setReassignTarget(null)} onDone={reload} />
    </div>
  );
}
